import { type Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';

import { StripeLogger } from './logger';

import type { StripeLoadSource } from '../types';

/**
 * Retries loading Stripe up to 5 times when Stripe explicitly rejects the promise.
 * Otherwise, reject the caller if it hasn't loaded within 15 seconds.
 * Retrying a non rejected promise does nothing, the original promise is reused by Stripe internally.
 */
export async function loadStripeWithRetry(
  publishableKey: string,
  source: StripeLoadSource,
): Promise<Stripe | null> {
  const loadAttemptId = getLoadAttemptId();

  return await withTimeout({
    promise: retryStripeLoading(publishableKey, source, loadAttemptId),
    loadAttemptId,
    source,
  });
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function retryStripeLoading(
  publishableKey: string,
  source: StripeLoadSource,
  loadAttemptId: string,
) {
  const startTime = performance.now();

  for (let attemptNumber = 1; attemptNumber <= MAX_ATTEMPTS; attemptNumber++) {
    try {
      const stripe = await loadStripe(publishableKey, STRIPE_LOAD_OPTIONS);

      if (stripe) {
        StripeLogger.logStripeLoadSuccess({
          source,
          duration: performance.now() - startTime,
          attemptNumber,
          loadAttemptId,
        });
      }

      return stripe;
    } catch (error) {
      StripeLogger.logStripeLoadError({
        source,
        loadAttemptId,
        attemptNumber,
        error: error as Error,
      });
    }
  }

  StripeLogger.logStripeLoadTooManyRejections({ source, loadAttemptId });

  return Promise.reject('Something went wrong while loading the payment provider.');
}

/**
 * If the promise does not resolve within the timeout, reject with the given message.
 * This can happen if `loadStripe` neither resolves nor rejects, leading to a stuck state.
 * Calling `loadStripe()` again in this scenario has no effect as the original promise is reused by Stripe internally.
 */
async function withTimeout<T>(params: WithTimeoutParams<T>): Promise<T> {
  const { promise, loadAttemptId, source } = params;

  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<T>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      StripeLogger.logStripeLoadTimeout({ source, loadAttemptId });
      reject(new Error('Timeout loading Stripe'));
    }, TIMEOUT_MS);
  });

  try {
    return await Promise.race([promise, timeout]);
  } catch (error) {
    return Promise.reject(error);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

/**
 * For identifying the load attempt in datadog.
 */
function getLoadAttemptId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type WithTimeoutParams<T> = {
  promise: Promise<T>;
  loadAttemptId: string;
  source: StripeLoadSource;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_ATTEMPTS = 5;
const TIMEOUT_MS = 15000;
const STRIPE_LOAD_OPTIONS = {
  developerTools: {
    assistant: {
      enabled: false,
    },
  },
};
